import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CardMedia,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Payment,
  Home,
  LocationOn,
  Add,
  Close,
  CheckCircle,
  CreditCard,
  Upload,
  Warning,
  LocalHospital,
  ShoppingCart,
  ShoppingBag,
  ArrowBack
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import SecuredLayout from '../components/layouts/SecuredLayout';
import useUserStore from '../stores/userStore';
import userService from '../services/userService';

const Checkout = () => {
  // Zustand store
  const { cart, cartCount, fetchCart, plusMinus, deletePCart, deleteOCart, userProfile } = useUserStore();
  const navigate = useNavigate();
  
  // Step management
  const [activeStep, setActiveStep] = useState(0);
  
  // Prescription step
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Address step
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [addAddressDialog, setAddAddressDialog] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [newAddress, setNewAddress] = useState({
    name: '',
    addressline1: '',
    addressline2: '',
    area: '',
    state: '',
    postal_code: '',
    city: '',
    landmark: ''
  });

  // Payment step
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [devBypass, setDevBypass] = useState(false);
  
  // Order details
  const [orderDetails, setOrderDetails] = useState(null);
  const [prescriptionId, setPrescriptionId] = useState(null);
  
  // General state
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [duplicatePrescriptionItems, setDuplicatePrescriptionItems] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  
  // Step labels
  const steps = [
    'Prescription Check',
    'Delivery Address',
    'Payment',
    'Order Confirmation'
  ];

  // Check if prescription is required
  const requiresPrescription = cart.some(item => 
    (item.product?.prescription_required) || (item.otc?.prescription_required)
  );

  // Check for duplicate prescription-required items
  const checkForDuplicates = () => {
    const prescriptionItems = cart.filter(item => 
      (item.product?.prescription_required) || (item.otc?.prescription_required)
    );
    
    // Group items by product ID
    const productGroups = {};
    prescriptionItems.forEach(item => {
      const productId = item.product ? item.product.id : item.otc.id;
      const productType = item.product ? 'product' : 'otc';
      const key = `${productType}-${productId}`;
      
      if (!productGroups[key]) {
        productGroups[key] = [];
      }
      productGroups[key].push(item);
    });
    
    // Find duplicates (groups with more than 1 item)
    const duplicates = [];
    Object.keys(productGroups).forEach(key => {
      if (productGroups[key].length > 1) {
        duplicates.push({
          key,
          items: productGroups[key],
          product: productGroups[key][0].product || productGroups[key][0].otc,
          totalQuantity: productGroups[key].reduce((sum, item) => sum + item.quantity, 0)
        });
      }
    });
    
    return duplicates;
  };

  // Check for duplicates when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      const duplicates = checkForDuplicates();
      setDuplicatePrescriptionItems(duplicates);
      if (duplicates.length > 0 && activeStep === 0 && !showDuplicateDialog) {
        setShowDuplicateDialog(true);
      } else if (duplicates.length === 0) {
        setShowDuplicateDialog(false);
      }
    } else {
      setDuplicatePrescriptionItems([]);
      setShowDuplicateDialog(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length, activeStep]);

  // Fetch cart and addresses data
  useEffect(() => {
    fetchCartData();
    fetchAddressesData();
  }, []);

  const fetchCartData = async () => {
    try {
      await fetchCart();
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const fetchAddressesData = async () => {
    setIsAddressLoading(true);
    try {
      const response = await userService.getAllAddress();
      const addressesData = response.data || [];
      setAddresses(addressesData);
      
      // Set the first address as default if available
      if (addressesData.length > 0) {
        setDefaultAddress(addressesData[0]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load addresses. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsAddressLoading(false);
    }
  };

  // Step navigation handlers
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step) => {
    setActiveStep(step);
  };

  // Prescription handlers
  const handleUploadPrescription = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('prescription_image', selectedFile);

      const response = await userService.uploadPrescription(formData);
      setPrescriptionId(response.data.id);
      setSelectedFile(null);
      setSnackbar({
        open: true,
        message: 'Prescription uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading prescription:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.prescription_image?.[0] || 'Failed to upload prescription',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Address handlers
  const handleInputChange = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleAddAddress = async () => {
    // Validate required fields
    const errors = {};
    if (!newAddress.landmark || newAddress.landmark.trim() === '') {
      errors.landmark = 'This field may not be blank.';
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields.',
        severity: 'error'
      });
      return;
    }

    setIsAddressLoading(true);
    setFieldErrors({});
    
    try {
      const response = await userService.addAddress(newAddress);
      
      // Refresh the addresses list
      await fetchAddressesData();
      
      setNewAddress({
        name: '',
        addressline1: '',
        addressline2: '',
        area: '',
        state: '',
        postal_code: '',
        city: '',
        landmark: ''
      });
      setAddAddressDialog(false);
      setFieldErrors({});
      
      setSnackbar({
        open: true,
        message: 'Address added successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding address:', error);
      
      // Handle API validation errors
      if (error.response?.data) {
        const apiErrors = error.response.data;
        const fieldValidationErrors = {};
        
        // Parse field-level errors from API response
        Object.keys(apiErrors).forEach(field => {
          if (Array.isArray(apiErrors[field])) {
            fieldValidationErrors[field] = apiErrors[field][0];
          } else if (typeof apiErrors[field] === 'string') {
            fieldValidationErrors[field] = apiErrors[field];
          }
        });
        
        if (Object.keys(fieldValidationErrors).length > 0) {
          setFieldErrors(fieldValidationErrors);
        }
        
        // Show general error message
        const firstError = Object.values(fieldValidationErrors)[0] || 'Failed to add address. Please try again.';
        setSnackbar({
          open: true,
          message: firstError,
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to add address. Please try again.',
          severity: 'error'
        });
      }
    } finally {
      setIsAddressLoading(false);
    }
  };

  const handleSelectAddress = (address) => {
    setDefaultAddress(address);
  };

  const resetAddressForm = () => {
    setNewAddress({
      name: '',
      addressline1: '',
      addressline2: '',
      area: '',
      state: '',
      postal_code: '',
      city: '',
      landmark: ''
    });
    setFieldErrors({});
  };

  const handleContinueShopping = () => {
    navigate('/');
  };

  // Handle duplicate prescription items - remove all duplicates except first
  const handleRemoveDuplicates = async (duplicateGroup) => {
    try {
      setIsLoading(true);
      // Keep the first item, remove the rest
      const itemsToRemove = duplicateGroup.items.slice(1);
      
      for (const item of itemsToRemove) {
        if (item.product) {
          await deletePCart(item.product.id);
        } else if (item.otc) {
          await deleteOCart(item.otc.id);
        }
      }

      await fetchCartData();
      setShowDuplicateDialog(false);
      setSnackbar({
        open: true,
        message: `Removed ${itemsToRemove.length} duplicate item(s)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing duplicates:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove duplicates',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle duplicate prescription items - combine quantities
  const handleCombineDuplicates = async (duplicateGroup) => {
    try {
      setIsLoading(true);
      const firstItem = duplicateGroup.items[0];
      const totalQuantity = duplicateGroup.totalQuantity;
      
      // Update first item quantity to total
      const productId = firstItem.product ? firstItem.product.id : firstItem.otc.id;
      const productType = firstItem.product ? 'product' : 'otc';
      const cartIndex = cart.findIndex(cartItem => cartItem.id === firstItem.id);
      
      // Calculate how much to add
      const quantityToAdd = totalQuantity - firstItem.quantity;
      
      if (quantityToAdd > 0) {
        for (let i = 0; i < quantityToAdd; i++) {
          await plusMinus({
            productId,
            action: 'inc',
            source: 'cart',
            index: cartIndex,
            productType,
          });
        }
      }
      
      // Remove duplicate items
      const itemsToRemove = duplicateGroup.items.slice(1);
      for (const item of itemsToRemove) {
        if (item.product) {
          await deletePCart(item.product.id);
        } else if (item.otc) {
          await deleteOCart(item.otc.id);
        }
      }

      await fetchCartData();
      setShowDuplicateDialog(false);
      setSnackbar({
        open: true,
        message: 'Combined duplicate items successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error combining duplicates:', error);
      setSnackbar({
        open: true,
        message: 'Failed to combine duplicates',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `₹${price.toFixed(2)}`;
  };

  const getUnitPrice = (product) => {
    const price = parseFloat(product.price || 0);
    const bestPrice = parseFloat(product.best_price || 0);
    return bestPrice > 0 && bestPrice < price ? bestPrice : price;
  };

  const getLineMRP = (item) => {
    const product = item.product || item.otc;
    const price = parseFloat(product.price || 0);
    return price * item.quantity;
  };

  const getLineTotal = (item) => {
    const product = item.product || item.otc;
    return getUnitPrice(product) * item.quantity;
  };

  const getLineDiscount = (item) => {
    return getLineMRP(item) - getLineTotal(item);
  };

  const calculateCartMRP = () => {
    return cart.reduce((total, item) => total + getLineMRP(item), 0);
  };

  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + getLineTotal(item);
    }, 0);
  };

  const calculateDiscount = () => {
    return cart.reduce((total, item) => total + getLineDiscount(item), 0);
  };

  const calculateTotal = () => {
    return calculateCartTotal();
  };

  const calculateSavingsPercent = () => {
    const mrp = calculateCartMRP();
    const discount = calculateDiscount();
    return mrp > 0 ? Math.round((discount / mrp) * 100) : 0;
  };

  // Get product image helper
  const getProductImage = (images, isOTC) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return '/assets/logo_new.png';
    }
    
    // Return the first image URL directly since they're already complete URLs
    const firstImage = images[0];
    
    // If it's already a complete URL, return it as is
    if (typeof firstImage === 'string' && (firstImage.startsWith('http://') || firstImage.startsWith('https://'))) {
      return firstImage;
    }
    
    // Fallback for local images (if any)
    return isOTC ? `/assets/images_1mg_otc/${firstImage}` : `/assets/Drugs Images/${firstImage}`;
  };

  // Step 1: Prescription Check Component
  const renderPrescriptionStep = () => {
    if (!requiresPrescription) {
      return (
        <Box sx={{ 
          textAlign: 'center', 
          py: 5,
          px: 2
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}>
            <CheckCircle color="success" sx={{ fontSize: 48 }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            No Prescription Required
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All items in your cart are available without prescription.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main'
          }}
          icon={<Warning />}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Prescription Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 0 }}>
            Some items in your cart require a valid prescription to complete your purchase.
          </Typography>
        </Alert>

        {/* Cart items requiring prescription */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Items Requiring Prescription:
          </Typography>
          {cart.filter(item => 
            (item.product?.prescription_required) || (item.otc?.prescription_required)
          ).map((item) => {
            const product = item.product || item.otc;
            const isOTC = !!item.otc;
            
            return (
              <Card 
                key={item.id} 
                variant="outlined" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      backgroundColor: 'grey.50',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0
                    }}>
                      <CardMedia
                        component="img"
                        image={getProductImage(product.images, isOTC)}
                        alt={product.name}
                        sx={{ 
                          objectFit: 'contain',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                        onError={(e) => {
                          e.target.src = '/assets/logo_new.png';
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Qty: {item.quantity} | {formatPrice(getLineTotal(item))}
                      </Typography>
                    </Box>
                    <LocalHospital color="error" sx={{ fontSize: 28, flexShrink: 0 }} />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Prescription upload */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Upload Prescription:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              id="prescription-upload"
              type="file"
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />
            <label htmlFor="prescription-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                fullWidth
                sx={{ 
                  textTransform: 'none', 
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  borderStyle: 'dashed',
                  '&:hover': {
                    borderStyle: 'solid',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                Select Prescription Image
              </Button>
            </label>
            {selectedFile && (
              <Box sx={{ 
                p: 1.5, 
                backgroundColor: 'success.light', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 500 }}>
                  ✓ Selected: {selectedFile.name}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleUploadPrescription}
              disabled={!selectedFile || isLoading}
              startIcon={isLoading ? <CircularProgress size={18} /> : <Upload />}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                fontWeight: 500
              }}
            >
              Upload Prescription
            </Button>
          </Box>

          {prescriptionId && (
            <Alert 
              severity="success" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'success.main'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                ✓ Prescription uploaded successfully
              </Typography>
            </Alert>
          )}
        </Box>
      </Box>
    );
  };

  // Step 2: Address Selection Component
  const renderAddressStep = () => {
    if (isAddressLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Select Delivery Address
        </Typography>
        
        {addresses.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.main'
            }}
          >
            <Typography variant="body2">
              No addresses found. Please add a delivery address to continue.
            </Typography>
          </Alert>
        ) : defaultAddress && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.main'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <strong>Current Address:</strong> {defaultAddress.name} - {defaultAddress.addressline1}, {defaultAddress.city}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Card 
                variant="outlined"
                sx={(theme) => ({ 
                  cursor: 'pointer',
                  border: defaultAddress?.id === address.id ? '2px solid' : '1px solid',
                  borderColor: defaultAddress?.id === address.id ? 'primary.main' : 'divider',
                  backgroundColor: defaultAddress?.id === address.id ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)'
                  }
                })}
                onClick={() => handleSelectAddress(address)}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Radio 
                      checked={defaultAddress?.id === address.id}
                      onChange={() => handleSelectAddress(address)}
                      sx={{ mt: -0.5 }}
                      color="primary"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {address.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {address.addressline1}
                        {address.addressline2 && `, ${address.addressline2}`}<br />
                        {address.area}, {address.city}, {address.state}<br />
                        {address.postal_code}
                        {address.landmark && (
                          <>
                            <br />
                            <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              Instructions: {address.landmark}
                            </Box>
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              resetAddressForm();
              setAddAddressDialog(true);
            }}
            startIcon={<Add />}
            sx={{ 
              textTransform: 'none', 
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontWeight: 500
            }}
          >
            Add New Address
          </Button>
        </Box>
      </Box>
    );
  };

  // Razorpay helpers
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRazorpayPayment = async () => {
    if (!defaultAddress) {
      setSnackbar({ open: true, message: 'Please select a delivery address first', severity: 'error' });
      return;
    }

    setPaymentProcessing(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setSnackbar({ open: true, message: 'Failed to load Razorpay. Please try again.', severity: 'error' });
        return;
      }

      const orderResponse = await userService.createRazorpayOrder();
      const { razorpay_order_id, amount, currency, key_id } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Pharmaman',
        description: 'Order Payment',
        order_id: razorpay_order_id,
        handler: async (response) => {
          setPaymentProcessing(false);
          setIsLoading(true);
          try {
            const verifyResponse = await userService.verifyRazorpayPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              prescription_id: prescriptionId,
              address_id: defaultAddress?.id
            });
            setOrderDetails(verifyResponse.data);
            await fetchCartData();
            setActiveStep(3);
            setSnackbar({
              open: true,
              message: 'Payment successful! Your order has been placed.',
              severity: 'success'
            });
          } catch (error) {
            console.error('Payment verification failed:', error);
            setSnackbar({
              open: true,
              message: error.response?.data?.error || 'Payment verification failed. Please contact support.',
              severity: 'error'
            });
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: userProfile?.name || userProfile?.first_name || '',
          email: userProfile?.email || '',
          contact: userProfile?.phone || ''
        },
        theme: {
          color: '#537ebc'
        },
        modal: {
          ondismiss: () => {
            setPaymentProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to initiate payment. Please try again.',
        severity: 'error'
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  // Dev bypass — skip Razorpay, create order directly
  const handleDevBypassPayment = async () => {
    if (!defaultAddress) {
      setSnackbar({ open: true, message: 'Please select a delivery address first', severity: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await userService.devBypassPayment({
        prescription_id: prescriptionId,
        address_id: defaultAddress?.id
      });
      setOrderDetails(response.data);
      await fetchCartData();
      setActiveStep(3);
      setSnackbar({
        open: true,
        message: 'Order placed successfully (dev bypass).',
        severity: 'success'
      });
    } catch (error) {
      console.error('Dev bypass failed:', error);
      setSnackbar({
        open: true, message: error.response?.data?.error || 'Dev bypass failed. Make sure DEBUG=True on the server.', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Payment Component
  const renderPaymentStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Payment
      </Typography>

      <Card
        variant="outlined"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 3
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CreditCard sx={{ color: 'primary.contrastText', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Razorpay Secure Checkout
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pay using Cards, UPI, Net Banking, or Wallets
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">MRP Total</Typography>
            <Typography variant="body2">{formatPrice(calculateCartMRP())}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Discount</Typography>
            <Typography variant="body2" color="success.main">- {formatPrice(calculateDiscount())}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Delivery</Typography>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>FREE</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Total Payable
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {formatPrice(calculateTotal())}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={devBypass}
                onChange={(e) => setDevBypass(e.target.checked)}
                color="warning"
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
                Mark payment as completed (Dev bypass — skip Razorpay)
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            size="large"
            color={devBypass ? 'warning' : 'primary'}
            onClick={devBypass ? handleDevBypassPayment : handleRazorpayPayment}
            disabled={paymentProcessing || isLoading || !defaultAddress}
            startIcon={paymentProcessing || isLoading ? <CircularProgress size={20} color="inherit" /> : <Payment />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            {paymentProcessing ? 'Initiating Payment...' : isLoading ? 'Placing Order...' : devBypass ? `Complete Order (Dev Bypass)` : `Pay ${formatPrice(calculateTotal())}`}
          </Button>

          {!defaultAddress && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              Please select a delivery address before making payment.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  // Step 4: Order Confirmation Component
  const renderConfirmationStep = () => {
    const orderId = orderDetails?.id ? `ORD-${orderDetails.id}` : 'Processing...';
    const finalTotal = orderDetails?.total_price ? parseFloat(orderDetails.total_price) : calculateTotal();

    return (
      <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
        <Box sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          backgroundColor: 'success.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
          border: '3px solid',
          borderColor: 'success.main'
        }}>
          <CheckCircle color="success" sx={{ fontSize: 56 }} />
        </Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
          Order Placed Successfully!
        </Typography>
        <Box sx={{ 
          display: 'inline-block',
          p: 2,
          backgroundColor: 'primary.light',
          borderRadius: 2,
          mb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
            Order ID: {orderId}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Amount Paid: {formatPrice(finalTotal)}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          Your order has been placed and will be processed shortly. You will receive a confirmation email shortly.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/orderslist')}
            sx={{ 
              textTransform: 'none',
              px: 3,
              py: 1.2,
              borderRadius: 2,
              fontWeight: 500
            }}
          >
            View Orders
          </Button>
          <Button
            variant="contained"
            onClick={handleContinueShopping}
            startIcon={<ShoppingBag />}
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1.2,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            Continue Shopping
          </Button>
        </Box>
      </Box>
    );
  };

  const renderAddressForm = () => (
    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
            label="Address Name"
            placeholder="e.g., Home, Office, Apartment"
          value={newAddress.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          InputProps={{
              startAdornment: <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
          }}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
        <Grid item xs={12}>
        <TextField
          fullWidth
            label="Address Line 1"
            placeholder="Street address, building name"
          value={newAddress.addressline1}
          onChange={(e) => handleInputChange('addressline1', e.target.value)}
          InputProps={{
              startAdornment: <Home sx={{ mr: 1, color: 'primary.main' }} />
          }}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
        <Grid item xs={12}>
        <TextField
          fullWidth
            label="Address Line 2"
            placeholder="Apartment, suite, unit (optional)"
          value={newAddress.addressline2}
          onChange={(e) => handleInputChange('addressline2', e.target.value)}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
            label="Area/Locality"
            placeholder="Area or locality name"
          value={newAddress.area}
          onChange={(e) => handleInputChange('area', e.target.value)}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            placeholder="City name"
            value={newAddress.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="State"
            placeholder="State or province"
          value={newAddress.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Pincode"
            placeholder="6-digit postal code"
          value={newAddress.postal_code}
          onChange={(e) => handleInputChange('postal_code', e.target.value)}
          variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }
            }}
        />
      </Grid>
      
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2,
            bgcolor: 'secondary.light',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 2
          }}>
            <Warning sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              Ensure pincode is correct for delivery
            </Typography>
          </Box>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Delivery Instructions"
          placeholder="Special delivery instructions, landmarks, or notes for the delivery person"
          value={newAddress.landmark}
          onChange={(e) => handleInputChange('landmark', e.target.value)}
          error={!!fieldErrors.landmark}
          helperText={fieldErrors.landmark || 'Landmark or delivery instructions are required'}
          InputProps={{
            startAdornment: <Warning sx={{ mr: 1, color: fieldErrors.landmark ? 'error.main' : 'primary.main' }} />
          }}
          variant="outlined"
          multiline
          rows={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: fieldErrors.landmark ? 'error.main' : 'primary.main',
              },
            }
          }}
        />
      </Grid>
      </Grid>
    </Box>
  );

  // Check if cart is empty
  if (cart.length === 0) {
    return (
      <SecuredLayout 
        activeTab={2} 
        pageTitle="Checkout"
        contentCols={10}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            px: 2
          }}>
            <Box sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <ShoppingCart sx={{ fontSize: 64, color: 'grey.400' }} />
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
              Your cart is empty
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Add some items to your cart to proceed with checkout
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              startIcon={<ShoppingBag />}
              sx={{ 
                textTransform: 'none',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        </Container>
      </SecuredLayout>
  );
  }

  return (
    <SecuredLayout 
      activeTab={2} 
      pageTitle="Checkout"
      contentCols={10}
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/cart')}
            sx={{ 
              mb: 3, 
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            Back to Cart
          </Button>
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
            Checkout
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Complete your order in {steps.length} simple steps
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 320px' } }}>
          {/* Left side - Steps */}
          <Box>
            <Card 
              elevation={0}
              sx={{ 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                <Stepper 
                  activeStep={activeStep} 
                  orientation="vertical"
                  sx={{
                    '& .MuiStepLabel-root': {
                      '& .MuiStepLabel-label': {
                        fontSize: '1rem',
                        fontWeight: 600
                      }
                    },
                    '& .MuiStepContent-root': {
                      borderLeft: '2px solid',
                      borderColor: 'divider',
                      pl: 3,
                      ml: 1.5
                    }
                  }}
                >
                  {/* Step 1: Prescription Check */}
                  <Step>
                    <StepLabel 
                      onClick={() => handleStepClick(0)}
                      sx={{ cursor: 'pointer' }}
                    >
                      Prescription Check
                    </StepLabel>
                    <StepContent>
                      {renderPrescriptionStep()}
                      <Box sx={{ mb: 2, mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button 
                          variant="contained" 
                          onClick={handleNext}
                          disabled={requiresPrescription && !prescriptionId}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }
                          }}
                        >
                          {requiresPrescription && !prescriptionId ? 'Upload Prescription to Continue' : 'Continue'}
                        </Button>
                        <Button
                          onClick={() => navigate('/cart')}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            borderRadius: 2
                          }}
                        >
                          Back to Cart
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 2: Address Selection */}
                  <Step>
                    <StepLabel 
                      onClick={() => handleStepClick(1)}
                      sx={{ cursor: 'pointer' }}
                    >
                      Delivery Address
                    </StepLabel>
                    <StepContent>
                      {renderAddressStep()}
                      <Box sx={{ mb: 2, mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={!defaultAddress}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }
                          }}
                        >
                          Continue to Payment
                        </Button>
                        <Button
                          onClick={handleBack}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            borderRadius: 2
                          }}
                        >
                          Back
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 3: Payment */}
                  <Step>
                    <StepLabel 
                      onClick={() => handleStepClick(2)}
                      sx={{ cursor: 'pointer' }}
                    >
                      Payment
                    </StepLabel>
                    <StepContent>
                      {renderPaymentStep()}
                      <Box sx={{ mb: 2, mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          onClick={handleBack}
                          disabled={isLoading || paymentProcessing}
                          sx={{ 
                            textTransform: 'none',
                            px: 3,
                            py: 1.2,
                            borderRadius: 2
                          }}
                        >
                          Back
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 4: Confirmation */}
                  <Step>
                    <StepLabel>Order Confirmation</StepLabel>
                    <StepContent>
                      {renderConfirmationStep()}
                    </StepContent>
                  </Step>
                </Stepper>
              </CardContent>
            </Card>
          </Box>

          {/* Right side - Order Summary */}
          <Box>
            <Box sx={{ 
              position: { xs: 'static', md: 'sticky' }, 
              top: 20,
              height: { xs: 'auto', md: 'fit-content' }
            }}>
              <Card 
                elevation={0}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
                  p: 2.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}>
                    <ShoppingCart color="primary" />
                    Order Summary
                  </Typography>
                </Box>
                <CardContent sx={{ p: 3 }}>
                
                  {/* Cart Items */}
                  <Box sx={{ mb: 3 }}>
                    {cart.map((item, index) => {
                      const product = item.product || item.otc;
                      const isOTC = !!item.otc;
                      
                      return (
                        <Box 
                          key={item.id} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            mb: 2,
                            pb: 2,
                            borderBottom: index < cart.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              borderRadius: 1,
                              px: 1,
                              mx: -1
                            }
                          }}
                        >
                          <Box sx={{ 
                            width: 60, 
                            height: 60, 
                            backgroundColor: 'grey.50',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}>
                            <CardMedia
                              component="img"
                              image={getProductImage(product.images, isOTC)}
                              alt={product.name}
                              sx={{ 
                                objectFit: 'contain',
                                maxWidth: '100%',
                                maxHeight: '100%'
                              }}
                              onError={(e) => {
                                e.target.src = '/assets/logo_new.png';
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 600,
                              mb: 0.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4
                            }}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                            {getLineDiscount(item) > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                {formatPrice(getLineMRP(item))}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {formatPrice(getLineTotal(item))}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Order Totals */}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">MRP Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatPrice(calculateCartMRP())}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Discount</Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        - {formatPrice(calculateDiscount())}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">Delivery</Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        FREE
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: 'primary.light',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'primary.main'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 ,color: 'primary.contrastText' }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                      {formatPrice(calculateTotal())}
                    </Typography>
                  </Box>
                  {calculateDiscount() > 0 && (
                    <Box sx={{ 
                      mt: 2,
                      p: 1.5,
                      backgroundColor: 'success.light',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'success.main'
                    }}>
                      <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600, textAlign: 'center' }}>
                        You save {formatPrice(calculateDiscount())} ({calculateSavingsPercent()}%)
                      </Typography>
                    </Box>
                  )}
                    </CardContent>
                  </Card>
            </Box>
          </Box>
        </Box>

        {/* Add Address Dialog */}
        <Dialog 
          open={addAddressDialog} 
          onClose={() => {
            setAddAddressDialog(false);
            resetAddressForm();
          }} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText'
            }}>
              <Add />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add New Address
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {renderAddressForm()}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={() => {
                setAddAddressDialog(false);
                resetAddressForm();
              }} 
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddAddress}
              disabled={isAddressLoading}
              variant="contained" 
              color="primary"
              startIcon={isAddressLoading ? <CircularProgress size={20} /> : <Add />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              {isAddressLoading ? 'Adding...' : 'Add Address'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Prescription Items Dialog */}
        <Dialog 
          open={showDuplicateDialog} 
          onClose={() => setShowDuplicateDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              <Typography variant="h6">Duplicate Prescription Items Found</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                You have duplicate prescription-required items in your cart. Please choose how to fix this before proceeding:
              </Typography>
            </Alert>
            
            {duplicatePrescriptionItems.map((duplicate, index) => {
              const product = duplicate.product;
              return (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Found {duplicate.items.length} duplicate entries with total quantity: {duplicate.totalQuantity}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRemoveDuplicates(duplicate)}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove Duplicates
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleCombineDuplicates(duplicate)}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        Combine Quantities ({duplicate.totalQuantity})
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setShowDuplicateDialog(false);
                setSnackbar({
                  open: true,
                  message: 'Please fix duplicate items before checkout',
                  severity: 'warning'
                });
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </SecuredLayout>
  );
};

export default Checkout;